/**
 * Tearing Application Insights down when the user withdraws consent: stop the
 * instance without flushing its queue, and remove every identifier the SDK
 * left on this origin, also in a document that never started an instance.
 */
import { logger } from '@this/shared/dist/tools/logger.js';
/** Cookies the SDK sets on its origin: the persistent user id and the session id. */
const SDK_COOKIES = ['ai_user', 'ai_session'];
/**
 * Identifier of the SDK's sender channel (`BreezeChannelIdentifier` in
 * applicationinsights-core-js, not re-exported by applicationinsights-web).
 */
const SDK_CHANNEL_IDENTIFIER = 'AppInsightsChannelPlugin';
/** localStorage backup of the session id. */
const SDK_LOCAL_STORAGE_KEY = 'ai_session';
/** sessionStorage send buffers (pending and in-flight telemetry). */
const SDK_SESSION_STORAGE_PREFIXES = ['AI_buffer', 'AI_sentBuffer'];
/**
 * Stops Application Insights for good: nothing queued is sent, the instance is
 * torn down and `window.appInsights` is cleared so a later `initInsights`
 * starts fresh.
 */
export function unloadInsights() {
    // Nothing to stop in a document that never started the SDK.
    const insights = window.appInsights;
    if (!insights) {
        return;
    }
    // The sender only honours the `disableTelemetry` of its own channel config,
    // not the root value. With it set, the unload drops the queue instead of
    // flushing it.
    insights.updateCfg({ extensionConfig: { [SDK_CHANNEL_IDENTIFIER]: { disableTelemetry: true } } }, true);
    void insights.unload(false);
    window.appInsights = undefined;
}
/**
 * Removes every identifier the SDK may have left on this origin. Works without
 * an instance: a fresh document that learns of a withdrawn consent has none,
 * but may still carry the cookies of an earlier granted session.
 */
export function removeInsightsArtifacts() {
    // The SDK sets its cookies with `Secure; SameSite=None` on https. A
    // deletion is a write with a past expiry and needs the same attributes,
    // otherwise a cross-site document's write is discarded.
    const attributes = location.protocol === 'https:' ? '; Secure; SameSite=None' : '';
    for (const name of SDK_COOKIES) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${attributes}`;
    }
    // Storage access throws when the browser blocks site data for this
    // third-party frame. Then nothing of the SDK can be stored either, so the
    // teardown is complete; the failure is only worth a log line.
    try {
        localStorage.removeItem(SDK_LOCAL_STORAGE_KEY);
        for (const key of Object.keys(sessionStorage)) {
            if (SDK_SESSION_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
                sessionStorage.removeItem(key);
            }
        }
    }
    catch (error) {
        logger.warn('Storage unavailable while removing Application Insights artifacts:', error);
    }
}
